import React, { useState } from 'react';
import { Card, Row, Col, Badge, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import {
  formatMoleDate,
  getImageUrl,
  getFallbackImageUrl,
} from '../utils';
import { useDeleteUserMoleMutation, useUpdateUserMoleMutation } from '../../../store/apis/skinMolesApi';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import AddImageToMoleModal from './AddImageToMoleModal';

/**
 * Card component for displaying individual mole information
 * @param {Object} props - Component props
 * @param {Object} props.mole - Mole data object
 * @param {Array} props.moleImages - Array of images for this mole
 * @param {boolean} props.isUnassigned - Whether this mole is unassigned
 * @param {Function} props.onShowCarousel - Handler to show image carousel modal
 * @param {Function} props.onMoleDeleted - Handler to call after successful mole deletion
 */
const MoleCard = ({
  mole,
  moleImages = [],
  isUnassigned = false,
  onShowCarousel,
  onMoleDeleted,
}) => {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({
    name: mole.name || '',
    description: mole.description || '',
  });

  // Delete mutation
  const [deleteMole, { isLoading: isDeleting }] = useDeleteUserMoleMutation();
  
  // Update mutation
  const [updateMole, { isLoading: isUpdating }] = useUpdateUserMoleMutation();

  // Sort images by creation date (newest first, then oldest to see progression)
  const sortedImages = [...moleImages].sort((a, b) => new Date(b.created) - new Date(a.created));

  // Use first/reference image for thumbnail
  const referenceImage =
    moleImages.find((img) => img.uuid === mole.reference_image) || sortedImages[0];

  // Get the best available image URL for thumbnail
  const getDisplayImageUrl = () => {
    if (referenceImage) {
      return getImageUrl(referenceImage, false); // false = prefer thumbnail
    }
    return getFallbackImageUrl(mole);
  };

  const imageUrl = getDisplayImageUrl();

  // Delete handlers
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      console.log('🗑️ Starting mole deletion process for:', mole.mole_code);
      console.log('🔍 Mole UUID:', mole.uuid);
      console.log('🖼️ Associated images found:', sortedImages.length);

      // Use the new force_delete parameter - backend handles all image deletion
      console.log('🗑️ Deleting mole with force_delete=true...');
      await deleteMole({ uuid: mole.uuid }).unwrap();
      console.log('✅ Mole and all associated images deleted successfully');

      setShowDeleteModal(false);

      // Notify parent component to refresh data
      if (onMoleDeleted) {
        await onMoleDeleted();
      }
    } catch (error) {
      console.error('❌ Failed to delete mole:', error);
      console.error('❌ Error details:', {
        mole: mole.mole_code,
        moleUuid: mole.uuid,
      });
      // Error handling could be enhanced with toast notifications
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  // Add Image handlers
  const handleAddImageClick = () => {
    setShowAddImageModal(true);
  };

  const handleAddImageClose = () => {
    setShowAddImageModal(false);
  };

  const handleImageAdded = async () => {
    // Notify parent component to refresh data
    if (onMoleDeleted) {
      await onMoleDeleted();
    }
  };

  // Edit handlers
  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
    if (!isEditMode) {
      // Reset form data when entering edit mode
      setEditData({
        name: mole.name || '',
        description: mole.description || '',
      });
    }
  };

  const handleDataChange = (field, value) => {
    // Apply field-specific limits
    let limitedValue = value;
    if (field === 'name' && value.length > 100) {
      limitedValue = value.substring(0, 100);
    } else if (field === 'description' && value.length > 500) {
      limitedValue = value.substring(0, 500);
    }

    setEditData(prev => ({
      ...prev,
      [field]: limitedValue
    }));
  };

  const handleSave = async () => {
    try {
      console.log('💾 Saving mole data:', editData);
      
      await updateMole({ uuid: mole.uuid, ...editData }).unwrap();
      console.log('✅ Mole data updated successfully');

      setIsEditMode(false);

      // Notify parent component to refresh data
      if (onMoleDeleted) {
        await onMoleDeleted();
      }
    } catch (error) {
      console.error('❌ Failed to update mole:', error);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    // Reset form data to original mole data
    setEditData({
      name: mole.name || '',
      description: mole.description || '',
    });
  };

  // Helper function to get badge color based on mole_badge value
  const getMoleBadgeColor = (badge) => {
    if (!badge) return 'secondary';
    
    const badgeUpper = String(badge).toUpperCase();
    
    if (badgeUpper === 'CRITICAL') return 'danger';
    if (badgeUpper === 'HIGH') return 'danger';
    if (badgeUpper === 'MODERATE') return 'warning';
    if (badgeUpper === 'LOW') return 'success';
    if (badgeUpper === 'UNKNOWN') return 'info';
    if (badgeUpper === 'NOT_CLASSIFIED') return 'secondary';
    
    return 'secondary';
  };

  // Get mole badge value - check both mole and user_mole
  const moleBadge = mole.mole_badge || mole.user_mole?.mole_badge;
  // Get AI description - check both mole and user_mole
  const aiDescription = mole.description_ai || mole.user_mole?.description_ai;

  return (
    <Card className={`mb-3 mole-card ${isUnassigned ? 'border-warning' : ''}`}>
      <Card.Body>
        {/* Buttons header - above everything */}
        <div className='d-flex flex-column flex-md-row justify-content-end align-items-stretch align-items-md-center gap-2 mb-3 pb-2 border-bottom'>
          {isEditMode ? (
            <>
              <Button
                variant='success'
                size='sm'
                onClick={handleSave}
                disabled={isUpdating}
                className='d-flex align-items-center justify-content-center gap-1 px-3 py-2'
                style={{ fontSize: '0.8rem' }}
              >
                {isUpdating ? t('skinMoles.moleCard.saving') : `💾 ${t('skinMoles.moleCard.save')}`}
              </Button>
              <Button
                variant='outline-secondary'
                size='sm'
                onClick={handleCancel}
                disabled={isUpdating}
                className='d-flex align-items-center justify-content-center gap-1 px-3 py-2'
                style={{ fontSize: '0.8rem' }}
              >
                ❌ {t('skinMoles.moleCard.cancel')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant='outline-success'
                size='sm'
                onClick={handleEditToggle}
                className='d-flex align-items-center justify-content-center gap-1 px-3 py-2'
                style={{ fontSize: '0.8rem' }}
                title={t('skinMoles.moleCard.editTitle')}
              >
                ✏️ {t('skinMoles.moleCard.edit')}
              </Button>
              <Button
                variant='outline-primary'
                size='sm'
                onClick={handleAddImageClick}
                className='d-flex align-items-center justify-content-center gap-1 px-3 py-2'
                style={{ fontSize: '0.8rem' }}
                title={t('skinMoles.moleCard.addImageTitle')}
              >
                {t('skinMoles.moleCard.addImage')}
              </Button>
              <Button
                variant='outline-danger'
                size='sm'
                onClick={handleDeleteClick}
                className='d-flex align-items-center justify-content-center gap-1 px-3 py-2'
                style={{ fontSize: '0.8rem' }}
                title={t('skinMoles.moleCard.deleteTitle')}
              >
                {t('skinMoles.moleCard.delete')}
              </Button>
            </>
          )}
        </div>

        <Row>
          {/* Image Column - Full width on mobile, 4/12 on larger screens */}
          <Col xs={12} md={4} className="mb-3 mb-md-0">
            <div className='mole-image-container position-relative'>
              <img
                src={imageUrl}
                alt={`${mole.name || mole.description}`}
                className="mole-image"
                style={{
                  width: '100%',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6',
                  cursor: sortedImages.length > 0 ? 'pointer' : 'default',
                }}
                onClick={() =>
                  sortedImages.length > 0 && onShowCarousel && onShowCarousel(mole, sortedImages)
                }
                onError={(e) => {
                  e.target.src = '/api/placeholder/150/150';
                }}
              />

              {/* Image counter badge */}
              {sortedImages.length > 1 && (
                <Badge
                  bg='primary'
                  className='position-absolute top-0 start-0 m-1'
                  style={{ fontSize: '0.7rem' }}
                >
                  📸 {sortedImages.length}
                </Badge>
              )}

              {/* Reference image indicator */}
              {referenceImage?.uuid === mole.reference_image && (
                <Badge
                  bg='success'
                  className='position-absolute top-0 end-0 m-1'
                  style={{ fontSize: '0.6rem' }}
                >
                  📌 REF
                </Badge>
              )}

              {/* View images button overlay */}
              {sortedImages.length > 0 && (
                <div
                  className='position-absolute bottom-0 start-0 end-0 p-2'
                  style={{
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
                    borderRadius: '0 0 4px 4px',
                  }}
                >
                  <Button
                    variant='light'
                    size='sm'
                    className='w-100'
                    style={{ fontSize: '0.7rem', opacity: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowCarousel && onShowCarousel(mole, sortedImages);
                    }}
                  >
                    🔍{' '}
                    {sortedImages.length === 1
                      ? t('skinMoles.moleCard.viewImage')
                      : sortedImages.length >= 5
                        ? t('skinMoles.moleCard.viewImagesPlural').replace(
                            '{{count}}',
                            sortedImages.length,
                          )
                        : t('skinMoles.moleCard.viewImages').replace(
                            '{{count}}',
                            sortedImages.length,
                          )}
                  </Button>
                </div>
              )}

              {/* Unassigned warning */}
              {isUnassigned && (
                <Badge
                  bg='warning'
                  className='position-absolute'
                  style={{
                    fontSize: '0.7rem',
                    top: sortedImages.length > 1 ? '25px' : '5px',
                    right: referenceImage?.uuid === mole.reference_image ? '60px' : '5px',
                  }}
                >
                  ⚠️
                </Badge>
              )}
            </div>
          </Col>
          {/* Info Column - Full width on mobile, 8/12 on larger screens */}
          <Col xs={12} md={8} className='mole-info'>
            {/* Mole title and code */}
            <div className='mb-2'>
              {isEditMode ? (
                <div>
                  <Form.Control
                    type='text'
                    size='sm'
                    value={editData.name}
                    onChange={(e) => handleDataChange('name', e.target.value)}
                    placeholder={t('skinMoles.moleCard.nameLabel')}
                    className='mb-1'
                    maxLength={100}
                  />
                  <div className='d-flex justify-content-between align-items-center'>
                    <span className='mole-code'>{mole.mole_code}</span>
                    <small className='text-muted'>
                      {editData.name.length}/100
                    </small>
                  </div>
                </div>
              ) : (
                <>
                  <div className='d-flex align-items-center gap-2 mb-1 flex-wrap'>
                    <h6 className='mb-0'>{mole.name || t('skinMoles.moleCard.unnamedMole')}</h6>
                    {moleBadge && (
                      <div className='d-flex align-items-center gap-1'>
                        <small className='text-muted' style={{ fontSize: '0.75rem' }}>
                          {t('skinMoles.moleCard.riskLevel')}:
                        </small>
                        <Badge bg={getMoleBadgeColor(moleBadge)} className='fs-6'>
                          {t(`skinMoles.moleBadge.${moleBadge}`) || moleBadge}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <span className='mole-code'>{mole.mole_code}</span>
                </>
              )}
              {isUnassigned && (
                <div className='mt-1'>
                  <small className='text-warning fw-bold'>
                    {t('skinMoles.moleCard.noBodyPartAssigned')}
                  </small>
                </div>
              )}
            </div>
            {isEditMode ? (
              <div className='mb-2'>
                <Form.Control
                  as='textarea'
                  rows={3}
                  size='sm'
                  value={editData.description}
                  onChange={(e) => handleDataChange('description', e.target.value)}
                  placeholder={t('skinMoles.moleCard.descriptionLabel')}
                  maxLength={500}
                />
                <div className='d-flex justify-content-end'>
                  <small className='text-muted'>
                    {editData.description.length}/500
                  </small>
                </div>
              </div>
            ) : (
              mole.description && (
                <div className='mb-2'>
                  <small className='text-muted'>{mole.description}</small>
                </div>
              )
            )}
            {mole.distinctive_features && (
              <div className='mb-2'>
                <small className='features-text'>
                  {t('skinMoles.moleCard.features')} {mole.distinctive_features}
                </small>
              </div>
            )}
            {/* AI Description */}
            {aiDescription ? (
              <div className='mb-3'>
                <div className='d-flex align-items-center gap-2 mb-2'>
                  <i className='bi bi-robot' style={{ fontSize: '1.1rem', color: '#0d6efd' }}></i>
                  <strong className='text-primary'>{t('skinMoles.moleCard.aiDescription')}</strong>
                </div>
                <div 
                  className='p-3 rounded border' 
                  style={{ 
                    fontSize: '0.875rem',
                    backgroundColor: '#e7f3ff',
                    borderColor: '#0d6efd !important',
                    borderWidth: '2px',
                    boxShadow: '0 2px 4px rgba(13, 110, 253, 0.1)'
                  }}
                >
                  <Markdown>{aiDescription}</Markdown>
                </div>
              </div>
            ) : (
              <div className='mb-2'>
                <small className='text-muted'>
                  {t('skinMoles.moleCard.noAiDescription')}
                </small>
              </div>
            )}
            {isUnassigned && (
              <div className='mb-2'>
                <small className='text-info'>{t('skinMoles.moleCard.unassignedMoleTip')}</small>
              </div>
            )}
            {/* Image count and size info - stacked on mobile */}
            <div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-2'>
              <small className='text-muted mb-1 mb-md-0'>
                {sortedImages.length === 1
                  ? t('skinMoles.moleCard.imageCount').replace('{{count}}', sortedImages.length)
                  : sortedImages.length >= 5
                    ? t('skinMoles.moleCard.imageCountPlural2').replace(
                        '{{count}}',
                        sortedImages.length,
                      )
                    : t('skinMoles.moleCard.imageCountPlural').replace(
                        '{{count}}',
                        sortedImages.length,
                      )}
                {sortedImages.length > 1 && (
                  <>
                    {' • '}
                    <span
                      className='text-primary'
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowCarousel && onShowCarousel(mole, sortedImages);
                      }}
                    >
                      {t('skinMoles.moleCard.clickToViewTimeline')}
                    </span>
                  </>
                )}
              </small>
              {mole.approximate_size_mm && (
                <small className='text-muted'>📏 {mole.approximate_size_mm}mm</small>
              )}
            </div>

            {/* Show mole creation date - better mobile spacing */}
            <div className='mt-2'>
              <small className='text-muted d-block mb-1'>
                {t('skinMoles.moleCard.moleCreated')} {new Date(mole.created).toLocaleDateString()}
              </small>
              {sortedImages.length > 1 && (
                <small className='text-primary d-block'>
                  {t('skinMoles.moleCard.latestImage')} {formatMoleDate(sortedImages[0]?.created)}
                </small>
              )}
            </div>
          </Col>
        </Row>
      </Card.Body>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('skinMoles.moleCard.deleteMoleTitle')}
        message={
          <div>
            <p>
              {t('skinMoles.moleCard.deleteMoleMessage').replace('{{moleCode}}', mole.mole_code)}
            </p>
            <p>{t('skinMoles.moleCard.deleteMoleDetails')}</p>
            <ul className='mt-2 mb-0'>
              <li>
                {t('skinMoles.moleCard.deleteMoleRecord').replace(
                  '{{moleName}}',
                  mole.name || t('skinMoles.moleCard.unnamedMole'),
                )}
              </li>
              <li>
                {sortedImages.length === 1
                  ? t('skinMoles.moleCard.deleteMoleImages').replace(
                      '{{count}}',
                      sortedImages.length,
                    )
                  : sortedImages.length >= 5
                    ? t('skinMoles.moleCard.deleteMoleImagesPlural2').replace(
                        '{{count}}',
                        sortedImages.length,
                      )
                    : t('skinMoles.moleCard.deleteMoleImagesPlural').replace(
                        '{{count}}',
                        sortedImages.length,
                      )}
              </li>
            </ul>
            <div className='alert alert-info mt-2 mb-0'>
              <small>
                <strong>{t('skinMoles.moleCard.deleteMoleNote')}</strong>
              </small>
            </div>
          </div>
        }
        confirmText={
          isDeleting ? t('skinMoles.moleCard.deletingMole') : t('skinMoles.moleCard.deleteMole')
        }
        variant='danger'
        isLoading={isDeleting}
      />

      {/* Add Image Modal */}
      <AddImageToMoleModal
        show={showAddImageModal}
        onHide={handleAddImageClose}
        mole={mole}
        onImageAdded={handleImageAdded}
      />
    </Card>
  );
};

export default MoleCard;

